import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { logService } from '../lib/logService';

// =====================================================
// ERROR BOUNDARY — GLOBAL CRASH HANDLER
// Catches React render errors, logs to system_logs,
// and shows a friendly fallback UI instead of white screen.
// =====================================================

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const ErrorFallback: React.FC<{ error: Error | null }> = ({ error }) => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-950 p-4">
    <div className="max-w-md text-center">
      <div className="text-4xl mb-4">⚠️</div>
      <h1 className="text-xl font-bold text-white mb-2">
        Algo deu errado
      </h1>
      <p className="text-neutral-400 mb-6">
        Ocorreu um erro inesperado. Tente recarregar a página.
      </p>
      {import.meta.env.DEV && error && (
        <pre className="text-left text-xs text-red-400 bg-red-500/10 rounded-lg p-4 mb-6 overflow-auto max-h-48">
          {error.message}
        </pre>
      )}
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-2.5 bg-white text-black rounded-lg font-medium hover:bg-neutral-200 transition-colors"
      >
        Recarregar página
      </button>
    </div>
  </div>
);

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack },
      },
    });
    logService.error('react.crash', error.message, {
      stack: error.stack,
      component_stack: errorInfo.componentStack,
      error_name: error.name,
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}
